import {
  collection,
  doc,
  setDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  updateDoc,
  getDoc,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config';
import { deleteNoteFromVault } from './sync';
import { getGraphToken } from './auth';

const NOTES_COLLECTION = 'notes';

export interface MarginNote {
  id: string;
  label: string;
  content: string;
  uid: string;
  tenantId?: string; // Associated tenant for team collaboration
  visibility: 'private' | 'tenant_read' | 'tenant_edit';
  isGlobal: boolean;
  documentId: string;
  documentName?: string;
  sharedWith?: string[];
  createdAt: number;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentType?: string;
  attachmentPath?: string;
  excelShareUrl?: string;
  environment?: string; // e.g. 'staging' or 'production'
  templateId?: string; // Associated template ID for marketplace locking
  // How an Excel/PowerPoint note renders in the appendix:
  //  'snapshot' (default) — static preview (Excel table / PPT slide thumbnail)
  //  'link'     — a hyperlink to the original OneDrive file (follows its sharing permissions)
  //  'embed'    — an embedded copy of the file (Phase 2; falls back to snapshot until shipped)
  appendixMode?: 'snapshot' | 'link' | 'embed';
}

export const createNote = async (note: MarginNote) => {
  // Inject the current environment on creation
  const env = process.env.VITE_ENVIRONMENT || 'staging';
  await setDoc(doc(db, NOTES_COLLECTION, note.id), { ...note, environment: env });
};

export const getNote = async (id: string): Promise<MarginNote | null> => {
  const docRef = doc(db, NOTES_COLLECTION, id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data() as MarginNote;
  } else {
    return null;
  }
};

export const getUserNotes = async (uid: string, showAll: boolean = false) => {
  const userQuery = query(
    collection(db, NOTES_COLLECTION),
    where("uid", "==", uid)
  );
  const snapshot = await getDocs(userQuery);
  const currentEnv = process.env.VITE_ENVIRONMENT || 'staging';
  
  const notes = snapshot.docs.map(doc => doc.data() as MarginNote).filter(n => {
    if (showAll) return true;
    // If a note has no environment, assume it's a legacy 'production' note
    const noteEnv = n.environment || 'production';
    return noteEnv === currentEnv;
  });
  return notes.sort((a, b) => b.createdAt - a.createdAt);
};

export const getLibraryNotes = async (uid: string, tenantId: string | undefined, showAll: boolean = false) => {
  // 1. Fetch Personal Notes
  const personalQuery = query(
    collection(db, NOTES_COLLECTION),
    where("uid", "==", uid)
  );
  const personalSnap = await getDocs(personalQuery);
  const personalNotes = personalSnap.docs.map(d => d.data() as MarginNote);

  // 2. Fetch Team/Shared Notes (if tenantId exists)
  let teamNotes: MarginNote[] = [];
  if (tenantId) {
    const teamQuery = query(
      collection(db, NOTES_COLLECTION),
      where("tenantId", "==", tenantId)
    );
    try {
      const teamSnap = await getDocs(teamQuery);
      teamNotes = teamSnap.docs.map(d => d.data() as MarginNote).filter(n => n.visibility !== 'private');
    } catch (err) {
      console.warn("Failed to fetch team notes:", err);
    }
  }

  // 3. Merge and Deduplicate (Personal notes win on ID collisions)
  const combinedMap = new Map<string, MarginNote>();
  teamNotes.forEach(n => combinedMap.set(n.id, n));
  personalNotes.forEach(n => combinedMap.set(n.id, n));

  const currentEnv = process.env.VITE_ENVIRONMENT || 'staging';
  
  return Array.from(combinedMap.values())
    .filter(n => {
      if (showAll) return true;
      return (n.environment || 'production') === currentEnv;
    })
    .sort((a, b) => b.createdAt - a.createdAt);
};

export const updateNote = async (id: string, data: Partial<MarginNote>) => {
  await updateDoc(doc(db, NOTES_COLLECTION, id), data);
};

export const removeNote = async (id: string, token: string | null) => {
  const note = await getNote(id);
  if (note) {
    // 1. Cloud Sync: Delete from OneDrive
    // Use the provided token, but attempt a last-minute fetch if it's potentially stale
    const freshToken = token || (await getGraphToken());
    await deleteNoteFromVault(note, freshToken).catch(err => {
      console.warn("OneDrive deletion failed, proceeding:", err);
    });

    // 2. Storage Cleanup: Delete original attachment from Firebase Storage
    if (note.attachmentPath) {
      const storageRef = ref(storage, note.attachmentPath);
      await deleteObject(storageRef).catch(err => {
        console.warn("Firebase Storage cleanup failed, proceeding:", err);
      });
    }
  }
  
  // 3. Local Cleanup: Delete from Firestore
  await deleteDoc(doc(db, NOTES_COLLECTION, id));
};

export const deleteAttachment = async (path: string) => {
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
};

export const uploadAttachment = async (file: File, path: string): Promise<string> => {
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

// --- ENTERPRISE GOVERNANCE HELPERS ---

/**
 * Lists all users belonging to a specific Microsoft Tenant.
 */
export const getTenantUsers = async (tenantId: string) => {
  const q = query(collection(db, "users"), where("tenantId", "==", tenantId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ uid: d.id, ...(d.data() as any) }));
};

/**
 * Updates a user's role. Restricted by cloud security rules to Admins.
 */
export const updateUserRole = async (targetUid: string, role: string) => {
  await updateDoc(doc(db, "users", targetUid), { role });
};

/**
 * Checks if an email is on the departmental pilot whitelist.
 */
export const checkEmailWhitelist = async (tenantId: string, email: string) => {
  const docRef = doc(db, "tenants", tenantId, "governance", "pilot_whitelist", "entries", email.toLowerCase());
  const snap = await getDoc(docRef);
  return snap.exists();
};

/**
 * Adds an email to the pilot whitelist.
 */
export const addToWhitelist = async (tenantId: string, email: string) => {
  const docRef = doc(db, "tenants", tenantId, "governance", "pilot_whitelist", "entries", email.toLowerCase());
  await setDoc(docRef, { email: email.toLowerCase(), addedAt: Date.now() });
};

/**
 * Removes an email from the pilot whitelist.
 */
export const removeFromWhitelist = async (tenantId: string, email: string) => {
  const docRef = doc(db, "tenants", tenantId, "governance", "pilot_whitelist", "entries", email.toLowerCase());
  await deleteDoc(docRef);
};

/**
 * Lists all whitelisted emails for a specific tenant.
 */
export const getTenantWhitelist = async (tenantId: string) => {
  const q = collection(db, "tenants", tenantId, "governance", "pilot_whitelist", "entries");
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data().email as string);
};

/**
 * Bulk adds multiple emails to the pilot whitelist using batched writes.
 */
export const bulkAddToWhitelist = async (tenantId: string, emails: string[]) => {
  const batch = writeBatch(db);
  const normalizedEmails = Array.from(new Set(emails.map(e => e.trim().toLowerCase())));
  
  normalizedEmails.forEach(email => {
    if (!email) return;
    const docRef = doc(db, "tenants", tenantId, "governance", "pilot_whitelist", "entries", email);
    batch.set(docRef, { email: email, addedAt: Date.now() });
  });
  
  await batch.commit();
};
